import { explain, type Ledger } from '@projections/engine';
import { useMemo, useState } from 'react';
import { formatCurrency } from '../../design/format';
import {
  drillGroups,
  type LedgerViewRow,
  ledgerToRows,
  OUTFLOW_COLUMNS,
  STATEMENT_COLUMNS,
  type StatementColumn,
} from './ledgerView';

/**
 * THE LEDGER TABLE — the differentiator (PLAN.md M7): the drill-down rendered as a beautiful real
 * FINANCIAL STATEMENT on the warm-paper surface. Sticky header, tabular-nums everywhere, one row
 * per simulated year; click a year to EXPAND it into the engine's `explain(year)` bank-statement
 * breakdown plus a grouped per-account / per-category / per-tax view of that year.
 *
 * Reads ONE path's `Ledger` directly (the ledger-first invariant — the exact object the simulator
 * produced), so a value here is the simulator's value, not a re-derivation. The expanded view shows
 * the engine's OWN `explain()` text verbatim, so the on-screen statement is byte-identical to what
 * the CLI / API / research app print for the same year.
 */
export interface LedgerTableProps {
  ledger: Ledger;
}

export function LedgerTable({ ledger }: LedgerTableProps): React.ReactElement {
  const rows = useMemo(() => ledgerToRows(ledger), [ledger]);
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div className="ledger__statement card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="ledger__scroll">
        <table className="ledger__table">
          <thead>
            <tr>
              <th className="ledger__th-expand">
                <span className="sr-only">Expand</span>
              </th>
              {STATEMENT_COLUMNS.map((c) => (
                <th key={c} className={c === 'period' ? 'ledger__th-year' : 'ledger__th-num'}>
                  {columnLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <LedgerRowView
                key={r.year}
                row={r}
                ledger={ledger}
                expanded={expanded === r.year}
                onToggle={() => setExpanded(expanded === r.year ? null : r.year)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LedgerRowView({
  row,
  ledger,
  expanded,
  onToggle,
}: {
  row: LedgerViewRow;
  ledger: Ledger;
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const depleted = (row.cells.depleted ?? 0) > 0.5;
  const colSpan = STATEMENT_COLUMNS.length + 1;
  return (
    <>
      <tr
        className={`ledger__row${depleted ? ' ledger__row--depleted' : ''}${
          expanded ? ' ledger__row--expanded' : ''
        }`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td className="ledger__td-expand">
          <span className={`ledger__chevron${expanded ? ' ledger__chevron--open' : ''}`}>›</span>
        </td>
        {STATEMENT_COLUMNS.map((c) => (
          <td key={c} className={cellClass(c, row.cells[c] ?? 0)}>
            {renderCell(c, row.cells[c] ?? 0)}
          </td>
        ))}
      </tr>
      {expanded && (
        <tr className="ledger__explain-row">
          <td colSpan={colSpan}>
            <YearBreakdown ledger={ledger} year={row.year} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The expanded year: the engine's OWN bank-statement rendering verbatim (the ledger-first promise),
 * alongside a grouped per-account / per-category / per-tax breakdown of the same year for quick
 * scanning. Both read the same `Ledger` row, so they always agree.
 */
function YearBreakdown({ ledger, year }: { ledger: Ledger; year: number }): React.ReactElement {
  const groups = useMemo(() => drillGroups(ledger, year), [ledger, year]);
  return (
    <div className="ledger__breakdown">
      <div className="ledger__breakdown-statement">
        <div className="ledger__breakdown-caption">Statement · {year}</div>
        {/* The engine's own bank-statement text — identical wherever the compute ran. */}
        <pre className="ledger__explain">{explain(ledger, year)}</pre>
      </div>
      {groups.length > 0 && (
        <div className="ledger__breakdown-groups">
          {groups.map((g) => (
            <div className="ledger__drill-group" key={g.title}>
              <div className="ledger__drill-title">{g.title}</div>
              <dl className="ledger__drill-list">
                {g.lines.map((l) => (
                  <div className="ledger__drill-line" key={l.label}>
                    <dt>{l.label}</dt>
                    <dd className="num">{formatCurrency(l.value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function cellClass(column: StatementColumn, value: number): string {
  if (column === 'period') return 'ledger__td-year';
  let tone = '';
  if (value !== 0) {
    if (OUTFLOW_COLUMNS.has(column)) tone = ' num--neg';
    else if (column === 'endBalance' || column === 'startBalance') tone = '';
    else tone = ' num--pos';
  }
  return `ledger__td-num num${tone}`;
}

function renderCell(column: StatementColumn, value: number): string {
  if (column === 'period') return String(value);
  if (value === 0) return formatCurrency(0);
  if (OUTFLOW_COLUMNS.has(column)) return `−${formatCurrency(Math.abs(value))}`;
  return formatCurrency(value);
}

function columnLabel(column: StatementColumn): string {
  switch (column) {
    case 'period':
      return 'Year';
    case 'startBalance':
      return 'Start';
    case 'endBalance':
      return 'End';
    default:
      return column.charAt(0).toUpperCase() + column.slice(1);
  }
}
